/**
 * Chapter list inside a theme (admin view).
 * Route: /admin/courses/:track/:themeSlug
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Trash,
  PencilSimple,
  ArrowLeft,
  FileText,
  CircleDashed,
  CheckCircle,
  DotsSixVertical,
} from "@phosphor-icons/react";
import { coursesApi, coursesAdminApi } from "../../api/courses";
import { getTrackLabel } from "../../lib/tracks";
import ConfirmDialog from "../../components/courses/ConfirmDialog";
import SortableList from "../../components/courses/SortableList";

function NewChapterRow({ themeId, onCreated }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      const trimmed = title.trim();
      if (!trimmed) return;
      setSaving(true);
      try {
        await coursesAdminApi.createChapter(themeId, trimmed);
        setTitle("");
        onCreated?.();
      } finally {
        setSaving(false);
      }
    },
    [themeId, title, onCreated],
  );

  return (
    <form
      onSubmit={submit}
      data-testid="admin-chapter-new-form"
      className="flex items-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 bg-white px-4 py-3"
    >
      <Plus size={18} weight="bold" className="text-slate-400 shrink-0" />
      <input
        type="text"
        data-testid="admin-chapter-new-input"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t("admin.courses.new_chapter_placeholder")}
        className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400"
      />
      <button
        type="submit"
        data-testid="admin-chapter-new-submit"
        disabled={saving || !title.trim()}
        className="rounded-full bg-slate-900 px-4 py-2 text-xs font-medium text-white disabled:opacity-40 hover:bg-slate-800"
      >
        {t("admin.courses.add_chapter")}
      </button>
    </form>
  );
}

function ChapterRow({ track, themeSlug, chapter, dragHandle, onChanged }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(chapter.title);
  const [confirm, setConfirm] = useState(false);

  const save = useCallback(async () => {
    const trimmed = title.trim();
    if (!trimmed || trimmed === chapter.title) {
      setEditing(false);
      setTitle(chapter.title);
      return;
    }
    await coursesAdminApi.updateChapter(chapter.id, { title: trimmed });
    setEditing(false);
    onChanged?.();
  }, [title, chapter.id, chapter.title, onChanged]);

  const remove = useCallback(async () => {
    await coursesAdminApi.deleteChapter(chapter.id);
    setConfirm(false);
    onChanged?.();
  }, [chapter.id, onChanged]);

  const isPublished = chapter.status === "published";

  return (
    <div
      data-testid={`admin-chapter-${chapter.slug}`}
      className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-3 hover:border-slate-900 transition-colors"
    >
      <button
        type="button"
        ref={dragHandle?.setActivatorNodeRef}
        {...(dragHandle?.attributes || {})}
        {...(dragHandle?.listeners || {})}
        data-testid={`admin-chapter-drag-${chapter.slug}`}
        aria-label="Réorganiser"
        className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-slate-400 hover:text-slate-700 touch-none"
      >
        <DotsSixVertical size={18} weight="bold" />
      </button>

      <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
        <FileText size={18} weight="regular" />
      </span>

      {editing ? (
        <input
          autoFocus
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") {
              setEditing(false);
              setTitle(chapter.title);
            }
          }}
          className="flex-1 bg-transparent outline-none text-sm font-medium text-slate-900 border-b border-slate-300"
        />
      ) : (
        <Link
          to={`/admin/courses/${track}/${themeSlug}/${chapter.slug}`}
          className="flex-1 text-sm font-medium text-slate-900 hover:underline"
          data-testid={`admin-chapter-link-${chapter.slug}`}
        >
          {chapter.title}
        </Link>
      )}

      <span
        className={`inline-flex items-center gap-1 text-xs font-medium ${
          isPublished ? "text-emerald-600" : "text-amber-600"
        }`}
        data-testid={`admin-chapter-status-${chapter.slug}`}
      >
        {isPublished ? <CheckCircle size={14} weight="fill" /> : <CircleDashed size={14} weight="bold" />}
        {isPublished ? t("admin.courses.status_published") : t("admin.courses.status_draft")}
      </span>

      <button
        type="button"
        onClick={() => setEditing(true)}
        aria-label="Renommer"
        className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
      >
        <PencilSimple size={16} />
      </button>
      <button
        type="button"
        onClick={() => setConfirm(true)}
        aria-label="Supprimer"
        className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-50 text-red-600"
      >
        <Trash size={16} />
      </button>

      <ConfirmDialog
        open={confirm}
        title={t("admin.courses.delete_chapter_title")}
        description={t("admin.courses.delete_chapter_desc")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        destructive
        onConfirm={remove}
        onCancel={() => setConfirm(false)}
      />
    </div>
  );
}

export default function AdminTheme() {
  const { t } = useTranslation();
  const { track, themeSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await coursesApi.theme(track, themeSlug);
      setData(res);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [track, themeSlug]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <p className="text-sm text-slate-500">{t("common.loading")}</p>;
  }
  if (error || !data) {
    return <p className="text-sm text-red-600">{t("admin.courses.theme_not_found")}</p>;
  }

  const { theme, chapters } = data;
  return (
    <div data-testid="admin-theme-page" className="space-y-8">
      <Link
        to={`/admin/courses?track=${track}`}
        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
        data-testid="admin-theme-back"
      >
        <ArrowLeft size={14} weight="bold" />
        {getTrackLabel(track, t)}
      </Link>

      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          {t("admin.courses.theme_kicker")}
        </p>
        <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
          {theme.title}
        </h1>
        <p className="mt-3 text-sm text-slate-600">
          {chapters.length} {t("admin.courses.chapters")}
        </p>
      </header>

      <div className="space-y-3">
        {chapters.length === 0 && (
          <p className="text-sm text-slate-500">{t("admin.courses.no_chapter_yet")}</p>
        )}
        {chapters.length > 0 && (
          <SortableList
            items={chapters}
            onReorder={async (nextChapters) => {
              setData((d) => ({ ...d, chapters: nextChapters }));
              const items = nextChapters.map((ch, idx) => ({
                id: ch.id,
                order: idx + 1,
              }));
              try {
                await coursesAdminApi.reorderChapters(items);
              } catch {
                load();
              }
            }}
            renderRow={(chapter, dragHandle) => (
              <ChapterRow
                key={chapter.id}
                track={track}
                themeSlug={themeSlug}
                chapter={chapter}
                dragHandle={dragHandle}
                onChanged={load}
              />
            )}
          />
        )}
        <NewChapterRow themeId={theme.id} onCreated={load} />
      </div>
    </div>
  );
}
